import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { useMpDungeonStore } from "../store/useMpDungeonStore";
import { useGameStore } from "../store/useGameStore";
import { FormattedNumber } from "../components/FormattedNumber";
import { PlayerProfileModal } from "../components/PlayerProfileModal";
import { leaderboardApi } from "../api/leaderboard";
import { formatCountdown } from "../store/useDuelStore";
import { CLASS_LABEL } from "../utils/assets";
import type { PlayerProfile } from "../types";

function formatCountdownLocal(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function MpDungeonsScreen() {
  const { hero, user, setScreen } = useGameStore();
  const {
    config,
    myRoom,
    rooms,
    loading,
    error,
    loadConfig,
    refreshRooms,
    createRoom,
    joinRoom,
    leaveRoom,
    setReady,
    startDungeon,
    kickMember,
  } = useMpDungeonStore();

  const [level, setLevel] = useState(40);
  const [difficulty, setDifficulty] = useState<"normal" | "hard">("normal");
  const [countdown, setCountdown] = useState(myRoom?.seconds_remaining ?? 0);
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const heroLevel = config?.hero_level ?? hero?.level ?? 0;
  const levels = config?.levels ?? [20, 30, 40, 50, 60, 70, 80];

  const availableLevels = useMemo(
    () => levels.filter((lv) => heroLevel >= lv),
    [levels, heroLevel],
  );

  const openProfile = async (userId: number) => {
    setProfileLoading(true);
    try {
      const profile = await leaderboardApi.getPlayerProfile(userId);
      setSelectedPlayer(profile);
    } catch (e) {
      useMpDungeonStore.setState({
        error: e instanceof Error ? e.message : "Failed to load player profile",
      });
    } finally {
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    void loadConfig();
    void refreshRooms();
    const t = setInterval(() => void refreshRooms(), 5000);
    return () => clearInterval(t);
  }, [loadConfig, refreshRooms]);

  useEffect(() => {
    if (availableLevels.length === 0) return;
    if (!availableLevels.includes(level)) {
      setLevel(availableLevels[availableLevels.length - 1]);
    }
  }, [availableLevels, level]);

  useEffect(() => {
    setCountdown(myRoom?.seconds_remaining ?? 0);
  }, [myRoom?.seconds_remaining]);

  useEffect(() => {
    if (!myRoom || myRoom.status !== "waiting") return;
    const t = setInterval(() => setCountdown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [myRoom]);

  if (myRoom?.status === "waiting") {
    const me = myRoom.members.find((m) => m && user && m.user_id === user.id);
    return (
      <div className="p-4 space-y-4">
        <button type="button" onClick={() => setScreen("battle")} className="text-sm text-gray-400 hover:text-white">
          ← Back to Fight
        </button>

        <div className="text-center space-y-2">
          <div className="text-5xl">🏰</div>
          <h2 className="text-xl font-bold font-game text-gold-400">
            MP Dungeon Lv.{myRoom.dungeon_level} ({myRoom.difficulty})
          </h2>
          <p className="text-gray-400 text-sm">Up to 4 players · leader picks the path</p>
          <p className="text-gray-500 text-xs">Tap a player to view stats</p>
        </div>

        <div className="bg-kingdom-purple border border-kingdom-glow rounded-xl p-4 text-center">
          <div className="text-xs text-gray-400 uppercase">Room closes in</div>
          <div className="text-3xl font-black text-gold-400 tabular-nums">{formatCountdownLocal(countdown)}</div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          {myRoom.members.map((m, i) => (
            <button
              key={i}
              type="button"
              disabled={!m || profileLoading}
              onClick={() => m && void openProfile(m.user_id)}
              className={clsx(
                "rounded-xl border p-3 min-h-[5rem] flex flex-col justify-center text-left transition-colors",
                m
                  ? "border-kingdom-mid bg-kingdom-dark hover:border-kingdom-glow active:scale-[0.98]"
                  : "border-dashed border-gray-700 bg-kingdom-dark/40 cursor-default",
                profileLoading && m && "opacity-60",
              )}
            >
              {m ? (
                <>
                  <div className="font-bold text-white text-sm truncate">{m.name}</div>
                  <div className="text-xs text-gray-400">
                    {CLASS_LABEL[m.hero_class as keyof typeof CLASS_LABEL] ?? m.hero_class} Lv.{m.level}
                  </div>
                  <div className="text-[10px] text-kingdom-glow mt-1">{m.ready ? "✅ Ready" : "⏳ Not ready"}</div>
                </>
              ) : (
                <div className="text-center text-gray-600 text-xs w-full">Empty slot</div>
              )}
            </button>
          ))}
        </div>

        {!me?.ready && (
          <button
            type="button"
            disabled={loading}
            onClick={() => void setReady(true)}
            className="w-full py-3 rounded-xl bg-kingdom-glow text-black font-bold disabled:opacity-50"
          >
            Ready
          </button>
        )}
        {me?.ready && (
          <button
            type="button"
            disabled={loading}
            onClick={() => void setReady(false)}
            className="w-full py-3 rounded-xl border border-kingdom-light text-gray-300 font-semibold"
          >
            Cancel ready
          </button>
        )}

        {myRoom.is_leader && myRoom.can_start && (
          <button
            type="button"
            disabled={loading}
            onClick={() => void startDungeon()}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-gold-500 to-gold-600 text-black font-bold"
          >
            Start dungeon
          </button>
        )}

        {myRoom.is_leader && !myRoom.can_start && (
          <p className="text-center text-xs text-gray-500">Waiting for all players to ready up…</p>
        )}

        <button
          type="button"
          disabled={loading}
          onClick={() => void leaveRoom()}
          className="w-full py-2 rounded-xl border border-rose-500/50 text-rose-300 text-sm"
        >
          {myRoom.is_host ? "Cancel room" : "Leave room"}
        </button>

        {selectedPlayer && (
          <PlayerProfileModal
            profile={selectedPlayer}
            onClose={() => setSelectedPlayer(null)}
            footer={
              myRoom.is_leader &&
              selectedPlayer.user_id !== user?.id && (
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => {
                    void kickMember(selectedPlayer.user_id).then(() => setSelectedPlayer(null));
                  }}
                  className="w-full py-2.5 rounded-xl border border-rose-500/60 text-rose-300 text-sm font-semibold hover:bg-rose-900/30"
                >
                  Kick from room
                </button>
              )
            }
          />
        )}
      </div>
    );
  }

  const enabled = config?.enabled !== false;
  const canEnterToday = config?.can_enter_today !== false;
  const canCreate =
    enabled && canEnterToday && availableLevels.length > 0 && availableLevels.includes(level);

  return (
    <div className="p-4 space-y-4 pb-8">
      <button type="button" onClick={() => setScreen("battle")} className="text-sm text-gray-400 hover:text-white">
        ← Back to Fight
      </button>

      <div className="text-center">
        <h2 className="text-xl font-bold font-game text-gold-400">🏰 MP Dungeons</h2>
        <p className="text-gray-400 text-sm mt-1">Party up to 4 · shards & legendary stones</p>
      </div>

      {!enabled && (
        <div className="bg-amber-900/30 border border-amber-500/50 rounded-xl p-3 text-amber-200 text-sm text-center">
          MP dungeons are temporarily disabled. Ask an admin to enable them in game config.
        </div>
      )}

      {enabled && config?.daily_limit_enabled && (
        <div
          className={clsx(
            "rounded-xl border p-3 text-sm text-center",
            canEnterToday
              ? "bg-emerald-900/20 border-emerald-500/40 text-emerald-200"
              : "bg-rose-900/20 border-rose-500/40 text-rose-200",
          )}
        >
          {canEnterToday ? (
            <>
              Daily visit available
              {config.bonus_runs > 0 && ` (+${config.bonus_runs} scroll bonus)`}
            </>
          ) : (
            <>Daily visit used. Buy a Dungeon Scroll (💎500) in the shop and use it from inventory.</>
          )}
        </div>
      )}

      {error && (
        <div className="bg-rose-900/30 border border-rose-500 rounded-xl p-3 text-rose-300 text-sm">{error}</div>
      )}

      <div className="bg-kingdom-purple rounded-xl border border-kingdom-mid p-4 space-y-3">
        <div className="text-sm font-semibold text-white">Create room</div>
        <p className="text-xs text-gray-500">Your level: {heroLevel}. Requires hero Lv.{level} for selected dungeon.</p>
        <div className="flex flex-wrap gap-2">
          {levels.map((lv) => {
            const locked = heroLevel < lv;
            return (
              <button
                key={lv}
                type="button"
                disabled={locked}
                onClick={() => setLevel(lv)}
                className={clsx(
                  "px-3 py-1.5 rounded-lg text-sm font-bold border",
                  locked && "opacity-40 cursor-not-allowed border-gray-800 text-gray-600",
                  !locked && level === lv && "border-kingdom-glow bg-kingdom-glow/20 text-gold-300",
                  !locked && level !== lv && "border-gray-700 text-gray-400",
                )}
                title={locked ? `Requires hero Lv.${lv}` : undefined}
              >
                Lv.{lv}
              </button>
            );
          })}
        </div>
        <div className="flex gap-2">
          {(["normal", "hard"] as const).map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDifficulty(d)}
              className={clsx(
                "flex-1 py-2 rounded-lg text-sm font-bold border capitalize",
                difficulty === d ? "border-kingdom-glow text-gold-300" : "border-gray-700 text-gray-400",
              )}
            >
              {d}
            </button>
          ))}
        </div>
        <button
          type="button"
          disabled={loading || !canCreate}
          onClick={() => void createRoom(level, difficulty)}
          className="w-full py-3 rounded-xl bg-kingdom-glow text-black font-bold disabled:opacity-50"
        >
          Create room (Lv.{level} · {difficulty})
        </button>
      </div>

      <div className="space-y-2">
        <div className="text-sm font-semibold text-gray-300">Open rooms</div>
        {rooms.length === 0 && <p className="text-gray-500 text-sm text-center py-4">No open rooms</p>}
        {rooms.map((r) => {
          const canJoin = heroLevel >= r.dungeon_level && canEnterToday;
          return (
            <button
              key={r.room_id}
              type="button"
              disabled={loading || !canJoin}
              onClick={() => void joinRoom(r.room_id)}
              className={clsx(
                "w-full text-left bg-kingdom-dark border rounded-xl p-3 active:scale-[0.99]",
                canJoin ? "border-kingdom-mid hover:border-kingdom-glow" : "border-gray-800 opacity-50",
              )}
            >
              <div className="flex justify-between items-start gap-2">
                <div>
                  <div className="font-bold text-white">
                    Lv.{r.dungeon_level} · {r.difficulty}
                  </div>
                  <div className="text-xs text-gray-400">
                    {r.host_name} · Lv.{r.host_level} · BR <FormattedNumber value={r.host_br} />
                  </div>
                  {!canJoin && heroLevel < r.dungeon_level && (
                    <div className="text-[10px] text-rose-400 mt-1">Requires Lv.{r.dungeon_level}</div>
                  )}
                </div>
                <div className="text-right text-xs text-gray-500">
                  <div>{r.member_count}/4</div>
                  <div>{formatCountdown(r.seconds_remaining)}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
